import { Component, Input, OnChanges } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Service, Subscription, SubscriptionContact } from '../Models/Subscription';
import { User } from '../Models/User';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { Contact } from '../Models/Contact';
import { Client } from '../Models/Client';

interface InvoiceFrequencyOption {
  code: string;
  name: string;
}

@Component({
  selector: 'app-admin-client-subscriptions',
  templateUrl: './admin-client-subscriptions.component.html',
  styleUrls: ['./admin-client-subscriptions.component.css'],
  standalone: false
})
export class AdminClientSubscriptionsComponent implements OnChanges {

  @Input() clientId!: number;
  @Input() loggedInUser?: User;
  @Input() clientName!: string;

  private loggedInUserTag = '';

  subscriptions: Subscription[] = [];
  serviceTypes: Service[] = [];
  
  contacts: Contact[] = [];
  clientContact: Client | undefined;

  invoicess: any[] = [];
  notifications: any[] = [];

  private tempNewId = -1;
  private tempNewContactId = -1;

  private editBackups = new Map<number, Partial<Subscription>>();
  private contactEditBackups = new Map<number, Partial<Contact>>();
  private clientBackup: Partial<Client> | undefined;

  contactsExpanded = false;
  invoicesExpanded = false;
  addressExpanded = false;
  subscriptionsExpanded = false;
  notificationsExpanded = false;

  private invoiceOptions: InvoiceFrequencyOption[] = [
    { code: 'A', name: 'Annual' },
    { code: 'M', name: 'Monthly' },
    { code: 'Q', name: 'Quarterly' }
  ];

  constructor(private http: HttpClient, private dialog: MatDialog) { }

  ngOnChanges() {
    if (this.clientId >0) {
      this.loggedInUserTag = `${this.loggedInUser?.lastName}, ${this.loggedInUser?.firstName}`;
      this.getAllSubscriptions();
      this.getServiceTypes();
      this.getAllContacts();
      this.getClientDetails();
      this.getClientInvoices();
      this.getClientNotifications();
    }
  }

  getServiceTypes() {
    this.http.get<Service[]>('/charitycentral/getservicetypes')
      .subscribe(result => this.serviceTypes = result || []);
  }

  getServiceTypeName(id: number) {
    return this.serviceTypes.find(x => x.id === id)?.name;
  }

  getInvoiceFrequency(): InvoiceFrequencyOption[] {
    return this.invoiceOptions;
  }

  private mapInvoiceFrequencyToCode(val?: string | null): string | undefined {
    if (!val) return undefined;
    const trimmed = String(val).trim();
    // If already a code
    const byCode = this.invoiceOptions.find(o => o.code.toLowerCase() === trimmed.toLowerCase());
    if (byCode) return byCode.code;
    // If a name, find code
    const byName = this.invoiceOptions.find(o => o.name.toLowerCase() === trimmed.toLowerCase());
    if (byName) return byName.code;
    // unknown - return as-is so backend can decide
    return trimmed;
  }

  getInvoiceFrequencyName(val?: string | null): string {
    if (!val) return '';
    const trimmed = String(val).trim();
    const byCode = this.invoiceOptions.find(o => o.code.toLowerCase() === trimmed.toLowerCase());
    if (byCode) return byCode.name;
    const byName = this.invoiceOptions.find(o => o.name.toLowerCase() === trimmed.toLowerCase());
    if (byName) return byName.name;
    return trimmed;
  }

  getAllSubscriptions() {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('clientId', String(this.clientId));

    this.http.get<Subscription[]>('/charitycentral/GetAllSubscriptions', { params })
      .subscribe(result => {
        this.subscriptions = (result || []).map(r => {
          const rawContacts = (r as any).notificationContacts || [];
          // server now returns list of SubscriptionContact objects or ids; map to simple person id list
          const contactIds = (rawContacts || []).map((c: any) => {
            if (c === null || c === undefined) return c;
            if (typeof c === 'number') return c;
            if (c.contactId !== undefined) return c.contactId;
            if (c.ContactId !== undefined) return c.ContactId;
            if (c.personId !== undefined) return c.personId;
            if (c.PersonId !== undefined) return c.PersonId;
            if (c.id !== undefined) return c.id;
            return c;
          }).filter((x: any) => x !== undefined && x !== null) as number[];

          const mapped: any = {
            ...r,
            showItems: false,
            isEdit: false,
            notificationContacts: contactIds,
            invoiceFrequency: this.mapInvoiceFrequencyToCode((r as any).invoiceFrequency)
          };
          return mapped as Subscription;
        });
      });
  }

  getClientDetails() {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('clientId', String(this.clientId));

    this.http.get<Client[]>('/charitycentral/GetAllClients', { params })
      .subscribe(result => {
        const list = result || [];
        // find the client by id if server returns multiple
        const c = list.find(x => x.id === this.clientId) || (list.length >0 ? list[0] : undefined);
        if (c) {
          this.clientContact = { ...c };
        } else {
          this.clientContact = { id: this.clientId, name: this.clientName } as Client;
        }
      }, () => {
        // on error, ensure we have at least a minimal client object
        if (!this.clientContact) this.clientContact = { id: this.clientId, name: this.clientName } as Client;
      });
  }

  getAllContacts() {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('clientId', String(this.clientId));

    this.http.get<Contact[]>('/charitycentral/getallcontacts', { params })
      .subscribe(result => {
        this.contacts = result || this.contacts;
      });
  }

  getClientInvoices() {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('clientId', String(this.clientId));

    this.http.get<any[]>('/charitycentral/GetClientInvoices', { params })
      .subscribe(result => {
        this.invoicess = result || [];
      }, () => {
        this.invoicess = [];
      });
  }

  getClientNotifications() {
    const params = new HttpParams()
      .set('user', this.loggedInUserTag)
      .set('clientId', String(this.clientId));

    this.http.get<any[]>('/charitycentral/GetClientNotification', { params })
      .subscribe(result => {
        this.notifications = result || [];
      }, () => {
        this.notifications = [];
      });
  }

  addSubscription() {
    const available = this.serviceTypes.filter(st => !this.subscriptions.some(s => s.serviceTypeId === st.id && this.isSubscriptionActive(s)));
    const defaultServiceTypeId = (available.length >0) ? available[0].id : (this.serviceTypes.length >0 ? this.serviceTypes[0].id :0);

    const newSub: Subscription = {
      id: this.tempNewId--,
      clientId: this.clientId,
      name: '',
      serviceTypeId: defaultServiceTypeId,
      isEdit: true,
      showItems: false,
      startDate: new Date(),
      invoiceFrequency: this.mapInvoiceFrequencyToCode('M')
    } as Subscription;

    this.subscriptions.unshift(newSub);
  }

  addContact() {
    const newContact: Contact = {
      id: this.tempNewContactId--,
      clientId: this.clientId,
      jobTitle: '',
      forename: '',
      surname: '',
      groupName: '',
      email: '',
      telephone: '',
      hasLogin: false,
      isEdit: true,
      isGroupType: false
    } as Contact;

    this.contacts.unshift(newContact);
  }

  get isAnyContactEditing(): boolean {
    return this.contacts.some(c => c.isEdit);
  }

  editContact(contact: Contact) {
    this.contacts.forEach(c => c.isEdit = false);
    const backup = JSON.parse(JSON.stringify(contact)) as Partial<Contact>;
    this.contactEditBackups.set(contact.id, backup);
    contact.isGroupType = !!contact.groupName;
    contact.isEdit = true;
  }

  onContactTypeChange(contact: Contact) {
    if (contact.isGroupType) {
      contact.forename = '';
      contact.surname = '';
      contact.jobTitle = '';
    } else {
      contact.groupName = '';
    }
  }

  cancelContactEdit(contact: Contact) {
    if (contact.id <=0) {
      this.contacts = this.contacts.filter(c => c !== contact);
      this.contactEditBackups.delete(contact.id);
    } else {
      const backup = this.contactEditBackups.get(contact.id);
      if (backup) {
        if (backup.forename !== undefined) contact.forename = backup.forename as string;
        if (backup.surname !== undefined) contact.surname = backup.surname as string;
        if (backup.email !== undefined) contact.email = backup.email as string;
        if (backup.telephone !== undefined) contact.telephone = backup.telephone as string;
        this.contactEditBackups.delete(contact.id);
      }
      contact.isEdit = false;
    }
  }

  saveContact(contact: Contact) {
    const params = new HttpParams()
      .set('Contact', JSON.stringify(contact))
      .set('user', this.loggedInUserTag);

    if (contact.id <=0) {
      this.http.post<number>('/charitycentral/createcontact', null, { params })
        .subscribe(() => {
          this.contactEditBackups.delete(contact.id);
          this.getAllContacts();
        });
    } else {
      this.http.post<boolean>('/charitycentral/UpdateContactInfo', null, { params })
        .subscribe(() => {
          this.contactEditBackups.delete(contact.id);
          this.getAllContacts();
        });
    }
  }

  deleteContact(contact: Contact) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Delete Contact', message: 'Are you sure you want to delete this contact?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      const params = new HttpParams()
        .set('contactId', String(contact.id))
        .set('user', this.loggedInUserTag);

      if (contact.id <=0) {
        this.contacts = this.contacts.filter(c => c !== contact);
        return;
      }

      this.http.post<boolean>('/charitycentral/deletecontact', null, { params })
        .subscribe(success => {
          if (success) {
            this.contacts = this.contacts.filter(c => c.id !== contact.id);
          }
        });
    });
  }

  // Client address editing
  editClientAddress() {
    if (!this.clientContact) return;
    this.clientBackup = JSON.parse(JSON.stringify(this.clientContact)) as Partial<Client>;
    this.clientContact.isEdit = true;
  }

  addAddress() {
    if (!this.clientContact) {
      this.clientContact = {
        id: this.clientId,
        name: this.clientName,
        address: '',
        town: '',
        county: '',
        country: '',
        postcode: '',
        isEdit: true
      } as Client;
      this.clientBackup = JSON.parse(JSON.stringify(this.clientContact)) as Partial<Client>;
    } else {
      this.editClientAddress();
    }
  }

  cancelClientEdit() {
    if (!this.clientContact) return;
    if (this.clientContact.id <=0) {
      // remove temporary
      this.clientContact = undefined;
      this.clientBackup = undefined;
      return;
    }
    if (this.clientBackup) {
      this.clientContact.address = this.clientBackup.address;
      this.clientContact.town = this.clientBackup.town;
      this.clientContact.county = this.clientBackup.county;
      this.clientContact.country = this.clientBackup.country;
      this.clientContact.postcode = this.clientBackup.postcode;
      this.clientBackup = undefined;
    }
    this.clientContact.isEdit = false;
  }

  saveClientAddress() {
    if (!this.clientContact) return;
    const params = new HttpParams()
      .set('Client', JSON.stringify(this.clientContact))
      .set('user', this.loggedInUserTag);

    this.http.post<boolean>('/charitycentral/updateclient', null, { params })
      .subscribe(() => {
        this.clientContact!.isEdit = false;
        this.clientBackup = undefined;
        this.getClientDetails();
      }, () => {
        // on error we keep edit state
      });
  }


  editSubscription(sub: Subscription) {
    this.subscriptions.forEach(s => s.isEdit = false);
    const backup = JSON.parse(JSON.stringify(sub)) as Partial<Subscription>;
    this.editBackups.set(sub.id, backup);
    sub.isEdit = true;
  }

  cancelEdit(sub: Subscription) {
    if (sub.id <=0) {
      this.subscriptions = this.subscriptions.filter(s => s !== sub);
      this.editBackups.delete(sub.id);
    } else {
      const backup = this.editBackups.get(sub.id);
      if (backup) {
        if (backup.serviceTypeId !== undefined) sub.serviceTypeId = backup.serviceTypeId as number;
        if (backup.invoiceFrequency !== undefined) sub.invoiceFrequency = backup.invoiceFrequency as string;
        this.editBackups.delete(sub.id);
      }
      sub.isEdit = false;
    }
  }

  cancelAddSubscription(sub: Subscription) {
    this.cancelEdit(sub);
  }

  saveSubscription(sub: Subscription) {
    const payloadSub: any = { ...sub };
    // remove UI-only properties
    delete payloadSub.isEdit;
    delete payloadSub.showItems;


    const contacts = (sub.notificationContacts || []).map((pId: number) => ({ contactId: pId } as SubscriptionContact));
    payloadSub.notificationContacts = contacts;

    // ensure invoiceFrequency is the code
    payloadSub.invoiceFrequency = this.mapInvoiceFrequencyToCode(payloadSub.invoiceFrequency);

    const params = new HttpParams()
      .set('Subscription', JSON.stringify(payloadSub))
      .set('user', this.loggedInUserTag);

    if (sub.id <=0) {
      this.http.post<number>('/charitycentral/createsubscription', null, { params })
        .subscribe(() => {
          this.editBackups.delete(sub.id);
          this.getAllSubscriptions();
        });
    } else {
      this.http.post<boolean>('/charitycentral/updatesubscription', null, { params })
        .subscribe(() => {
          this.editBackups.delete(sub.id);
          this.getAllSubscriptions();
        });
    }
  }

  deleteSubscription(sub: Subscription) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Delete Subscription', message: 'Are you sure you want to delete this subscription?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      const params = new HttpParams()
        .set('subscriptionId', String(sub.id))
        .set('user', this.loggedInUserTag);

      if (sub.id <=0) {
        this.subscriptions = this.subscriptions.filter(s => s !== sub);
        return;
      }

      this.http.post<boolean>('/charitycentral/deletesubscription', null, { params })
        .subscribe(success => {
          if (success) {
            this.subscriptions = this.subscriptions.filter(s => s.id !== sub.id);
          }
        });
    });
  }

  get anyExpanded(): boolean {
    return this.subscriptions.some(s => !!s.showItems);
  }

  isAddDisabled(): boolean {
    if (!this.serviceTypes || this.serviceTypes.length ===0) return false;
    return this.serviceTypes.every(st => this.subscriptions.some(s => s.serviceTypeId === st.id && this.isSubscriptionActive(s)));
  }

  onDateChange(event: { value: Date | null }, sub: any) {
    sub.startDate = event.value;
  }

  getAvailableServiceTypes(sub: Subscription): Service[] {
    if (!this.serviceTypes || this.serviceTypes.length ===0) return [];
    return this.serviceTypes.filter(st => !this.subscriptions.some(s => s !== sub && s.serviceTypeId === st.id && this.isSubscriptionActive(s)));
  }

  isSubscriptionValid(sub: Subscription): boolean {
    if (!sub) return false;
    if (!sub.serviceTypeId || sub.serviceTypeId ===0) return false;
    if (!sub.startDate) return false;
    const dup = this.subscriptions.some(s => s !== sub && s.serviceTypeId === sub.serviceTypeId && this.isSubscriptionActive(s));
    return !dup;
  }

  isSubscriptionActive(sub: Subscription): boolean {
    return !sub.endDate;
  }

  unsubscribeSubscription(sub: Subscription) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Unsubscribe', message: 'Are you sure you want to deactivate ?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      const now = new Date();
      sub.endDate = now;

      const payloadSub: any = { ...sub };
      delete payloadSub.isEdit;
      delete payloadSub.showItems;
      // map notification contacts to SubscriptionContact shape (personId)
      payloadSub.notificationContacts = (sub.notificationContacts || []).map((pId: any) => ({ personId: pId } as SubscriptionContact));
      payloadSub.invoiceFrequency = this.mapInvoiceFrequencyToCode(payloadSub.invoiceFrequency);

      const params = new HttpParams()
        .set('Subscription', JSON.stringify(payloadSub))
        .set('user', this.loggedInUserTag);

      this.http.post<boolean>('/charitycentral/updatesubscription', null, { params })
        .subscribe(success => {
          if (!success) {
            sub.endDate = undefined;
          }
        }, () => {
          sub.endDate = undefined;
        });
    });
  }

  stopEvent(e: Event) { e.stopPropagation(); }
  stopKey(e: KeyboardEvent) { e.stopPropagation(); }

  getContactNamesForSubscription(sub: Subscription): string {
    if (!sub.notificationContacts || (sub.notificationContacts as any).length ===0) return '';

    const names = (sub.notificationContacts as any[]).map(contactOrId => {
      if (typeof contactOrId === 'number') {
        const p = this.contacts.find(c => c.id === contactOrId);
        return p ? (p.groupName || `${p.forename} ${p.surname}`.trim()) : '';
      }
      if (contactOrId && typeof contactOrId === 'object') {
        // Try known property names
        return (contactOrId.contactName as string) || (contactOrId.ContactName as string) || '';
      }
      return '';
    }).filter((n: string) => !!n);

    return names.join(', ');
  }

  isNamedService(serviceTypeId?: number): boolean {
    if (!serviceTypeId || !this.serviceTypes) return false;

    const service = this.serviceTypes.find(s => s.id === serviceTypeId);
    return service?.name?.toLowerCase() === 'named';
  }

  getServiceStyleIndex(serviceTypeId?: number): number {
    if (!serviceTypeId || !this.serviceTypes || this.serviceTypes.length ===0) return 1;
    const idx = this.serviceTypes.findIndex(s => s.id === serviceTypeId);
    if (idx < 0) return 1;
    // map to1..5
    return (idx %5) +1;
  }

}
